import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "../../../routes";
import { SearchBar } from "@legal-500/legal500-design-system";
import {
  CmsEmptyState,
  CmsFilterRow,
  CmsGroupedTable,
  CmsStackedBar,
  LanguageSwitcher,
} from "../../../components/ResearchCms";
import { ImportBpasDialog } from "../../../components/ResearchCms/ImportBpasDialog/ImportBpasDialog";
import { AddBpaModal } from "../../../components/ResearchCms/AddBpaModal/AddBpaModal";
import { CmsLayout } from "./components/CmsLayout";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { researchCms, fetchAllEditions } from "@/api/researchCms.service";
import type { EditionBpa } from "@/api/researchCms.service";
import { getTrafficLightColor } from "./utils/trafficLight";
import { resolveManageTarget } from "./utils/resolveManageTarget";
import { bpaProgressPercent, bpaReviewCounts } from "./utils/bpaProgress";
import type { CmsNavState } from "./types";
import styles from "./CmsPublicationPage.module.css";

const PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 300;

type ActiveFilter = { key: string; label: string };

type BpaRow = EditionBpa & {
  /** False when this edition's statistics could not be read, which is not the same as all-zero. */
  hasStats: boolean;
  submissionCount: number;
  approvedCount: number;
  pendingCount: number;
  readyForEditingCount: number;
  readyForTranslationCount: number;
  readyForReleaseCount: number;
};

export function CmsPublicationPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const { editionId: routeEditionId } = useParams<{ editionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as CmsNavState;

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedRankingEntityId = searchParams.get("rankingEntity") ?? "";
  const debouncedSearch = searchParams.get("search") ?? "";
  const statusFilter = searchParams.get("status") ?? "";
  const sortBy = searchParams.get("sort") ?? "name_asc";

  const [searchInput, setSearchInput] = useState(debouncedSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const editionId = routeEditionId!;
  const [currentPage, setCurrentPage] = useState(1);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importSourceEditionConfigId, setImportSourceEditionConfigId] = useState<string | null>(
    null,
  );
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [showAddBpa, setShowAddBpa] = useState(false);
  const showTranslation = searchParams.get("lang") === "en-GB";

  useEffect(() => {
    if (searchInput === debouncedSearch) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set("search", searchInput);
        else next.delete("search");
        return next;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput, debouncedSearch, setSearchParams]);

  const offset = (currentPage - 1) * PAGE_SIZE;

  const { data: editionData } = useQuery({
    queryKey: ["research-edition-stats", editionId],

    queryFn: async () => {
      const [statsResult, editionsResult] = await Promise.all([
        researchCms.getBpaStats({ editionId }),
        fetchAllEditions(),
      ]);
      const edition = editionsResult.find(e => e.editionId === editionId);
      return {
        statsMap: new Map(statsResult.researchBpaStats.map(s => [s.bpaId, s])),
        editionName: edition?.editionName ?? null,
        directoryId: edition?.directoryId ?? null,
      };
    },
  });

  const autoCreatedRef = useRef(false);
  useEffect(() => {
    if (autoCreatedRef.current) return;
    if (!editionData) return;
    if (editionData.directoryId) return;
    autoCreatedRef.current = true;
    researchCms
      .createEditionConfig({ editionId })
      .then(() => queryClient.invalidateQueries({ queryKey: ["research-edition-stats"] }))
      .catch(() => undefined);
  }, [editionData, editionId, queryClient]);

  const {
    data: bpaData,
    isLoading: bpaLoading,
    isFetching: bpaFetching,
    error,
  } = useQuery({
    queryKey: [
      "research-edition-bpas",
      editionId,
      debouncedSearch,
      selectedRankingEntityId,
      statusFilter,
      sortBy,
      offset,
    ],

    queryFn: () =>
      researchCms.getEditionBpas({
        editionId,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(selectedRankingEntityId ? { rankingEntityId: selectedRankingEntityId } : {}),
        ...(statusFilter
          ? {
              status: statusFilter as
                | "not_started"
                | "researching"
                | "editing"
                | "complete"
                | "archived",
            }
          : {}),
        ...(sortBy && sortBy !== "name_asc"
          ? { sort: sortBy as "name_desc" | "status_asc" | "status_desc" }
          : {}),
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const statsMap = editionData?.statsMap ?? new Map();

  const bpas: BpaRow[] = useMemo(
    () =>
      (bpaData?.editionBpas ?? []).map(bpa => {
        const stat = statsMap.get(bpa.bpaId);
        const outputName = bpa.outputName ?? null;
        const englishName = bpa.englishName ?? null;
        return {
          ...bpa,
          bpaName: showTranslation ? (englishName ?? bpa.bpaName) : (outputName ?? bpa.bpaName),
          hasStats: stat !== undefined,
          submissionCount: stat?.submissionCount ?? 0,
          approvedCount: stat?.approvedCount ?? 0,
          pendingCount: stat?.pendingCount ?? 0,
          readyForEditingCount: stat?.readyForEditingCount ?? 0,
          readyForTranslationCount: stat?.readyForTranslationCount ?? 0,
          readyForReleaseCount: stat?.readyForReleaseCount ?? 0,
        };
      }),
    [bpaData, statsMap, showTranslation],
  );
  const pagination = bpaData?.pagination ?? {
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
    hasMore: false,
  };
  const editionName = editionData?.editionName ?? "";
  const templateType = navState?.templateType ?? "conventional";
  const isRefetching = bpaFetching && !bpaLoading;

  const { data: rankingEntitiesData } = useQuery({
    queryKey: ["research-ranking-entities"],

    queryFn: async () => {
      const result = await researchCms.getRankingEntities({ limit: 2000 });
      return result.researchRankingEntities;
    },
  });

  const rankingEntityOptions = useMemo(() => {
    const entities = rankingEntitiesData ?? [];
    return entities
      .map(e => ({ value: e.id, label: e.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rankingEntitiesData]);

  const selectedRankingEntityName = useMemo(() => {
    if (!selectedRankingEntityId) return "";
    for (const opt of rankingEntityOptions) {
      if (opt.value === selectedRankingEntityId) return opt.label;
    }
    return "";
  }, [selectedRankingEntityId, rankingEntityOptions]);

  const { data: publicationsData } = useQuery({
    queryKey: ["research-publications-list"],
    queryFn: async () => {
      const result = await researchCms.getPublications();
      return result.publications;
    },
  });

  const manageTarget = useMemo(
    () => resolveManageTarget(publicationsData ?? [], editionId),
    [publicationsData, editionId],
  );

  const outputLanguage = useMemo(() => {
    for (const pub of publicationsData ?? []) {
      if (pub.editions.some(e => e.editionId === editionId)) return pub.outputLanguage;
    }
    return "en-GB";
  }, [publicationsData, editionId]);

  const hasTranslations = (bpaData?.editionBpas ?? []).some(
    bpa => bpa.outputName || bpa.englishName,
  );

  const editionsQuery = useQuery({
    queryKey: ["research-all-editions"],
    queryFn: () => fetchAllEditions(),
  });
  const importPublications = useMemo(() => {
    const allEditions = (editionsQuery.data ?? []).filter(e => e.editionId !== editionId);
    const pubs = publicationsData ?? [];
    const editionsByDir = new Map<string, typeof allEditions>();
    for (const e of allEditions) {
      if (!e.directoryId) continue;
      const list = editionsByDir.get(e.directoryId);
      if (list) list.push(e);
      else editionsByDir.set(e.directoryId, [e]);
    }
    return pubs
      .filter(p => editionsByDir.has(p.directoryId))
      .map(p => ({
        name: p.name,
        editions: (editionsByDir.get(p.directoryId) ?? []).map(e => ({
          id: e.id,
          editionId: e.editionId,
          editionName: e.editionName ?? null,
        })),
      }));
  }, [editionsQuery.data, editionId, publicationsData]);
  const hasImportableEditions = importPublications.some(p => p.editions.length > 0);

  const canImport = hasPermission("staff.research.create");
  const canAddBpa = hasPermission("staff.research.create");

  const linkedBpaIds = useMemo(
    () => new Set(editionData?.statsMap.keys() ?? []),
    [editionData?.statsMap],
  );

  function openImportDialog() {
    const directoryId = editionData?.directoryId;
    const samePub = importPublications.find(p =>
      p.editions.some(
        e => (editionsQuery.data ?? []).find(d => d.id === e.id)?.directoryId === directoryId,
      ),
    );
    const defaultEdition = samePub?.editions[0] ?? importPublications[0]?.editions[0] ?? null;
    setImportSourceEditionConfigId(defaultEdition?.id ?? null);
    setImportResult(null);
    setImportError(null);
    setShowImportDialog(true);
  }

  async function handleImport() {
    if (!importSourceEditionConfigId) return;
    const sourceEdition = (editionsQuery.data ?? []).find(
      e => e.id === importSourceEditionConfigId,
    );
    if (!sourceEdition) return;

    setIsImporting(true);
    try {
      const result = await researchCms.importEditionBpas(sourceEdition.editionId, editionId);
      setShowImportDialog(false);
      setImportResult(
        `Imported ${result.created} practice area${result.created !== 1 ? "s" : ""}${result.skipped > 0 ? ` (${result.skipped} already linked, skipped)` : ""}.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["research-edition-bpas"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["research-edition-stats"],
      });
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Failed to import practice areas.");
      setShowImportDialog(false);
    } finally {
      setIsImporting(false);
    }
  }

  const setParam = useCallback(
    (key: string, value: string) => {
      setCurrentPage(1);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams],
  );

  const clearParam = useCallback(
    (key: string) => {
      if (key === "search") {
        setSearchInput("");
      }
      setParam(key, "");
    },
    [setParam],
  );

  const statusLabels: Record<string, string> = {
    not_started: "Not Started",
    researching: "Researching",
    editing: "Editing",
    complete: "Complete",
    archived: "Archived",
  };

  const sortLabels: Record<string, string> = {
    name_asc: "Name (A-Z)",
    name_desc: "Name (Z-A)",
    status_asc: "Status (Not Started first)",
    status_desc: "Status (Complete first)",
  };

  const activeFilters = useMemo(() => {
    const pills: ActiveFilter[] = [];
    if (selectedRankingEntityId) {
      pills.push({
        key: "rankingEntity",
        label: `Ranking Entity: ${selectedRankingEntityName}`,
      });
    }
    if (statusFilter) {
      pills.push({
        key: "status",
        label: `Status: ${statusLabels[statusFilter] ?? statusFilter}`,
      });
    }
    if (sortBy && sortBy !== "name_asc") {
      pills.push({
        key: "sort",
        label: `Sort: ${sortLabels[sortBy] ?? sortBy}`,
      });
    }
    return pills;
  }, [selectedRankingEntityId, selectedRankingEntityName, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(pagination.total / PAGE_SIZE));
  const startIndex = offset;
  const endIndex = Math.min(offset + PAGE_SIZE, pagination.total);

  const { grouped, ungrouped } = useMemo(() => {
    const groups: Record<string, BpaRow[]> = {};
    const noGroup: BpaRow[] = [];
    for (const bpa of bpas) {
      if (bpa.practiceGroupName) {
        if (!groups[bpa.practiceGroupName]) groups[bpa.practiceGroupName] = [];
        groups[bpa.practiceGroupName].push(bpa);
      } else {
        noGroup.push(bpa);
      }
    }
    return {
      grouped: Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])),
      ungrouped: noGroup,
    };
  }, [bpas]);

  const tableGroups = useMemo(
    () =>
      grouped.map(([groupName, groupBpas]) => ({
        name: groupName,
        count: groupBpas.length,
        countLabel: "practice areas",
        rows: groupBpas,
      })),
    [grouped],
  );

  const ungroupedTableGroup = useMemo(
    () => (ungrouped.length > 0 ? [{ name: "", rows: ungrouped }] : []),
    [ungrouped],
  );

  const columns = useMemo(
    () => [
      {
        key: "name",
        render: (bpa: BpaRow) => <span style={{ paddingLeft: 16 }}>{bpa.bpaName}</span>,
      },
      {
        key: "rankingEntity",
        width: "160px",
        render: (bpa: BpaRow) => (
          <span style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>
            {bpa.rankingEntityName}
          </span>
        ),
      },
      {
        key: "pct",
        width: "52px",
        render: (bpa: BpaRow) => {
          const pct = bpa.hasStats
            ? bpaProgressPercent({
                pendingCount: bpa.pendingCount,
                readyForEditingCount: bpa.readyForEditingCount,
                readyForReleaseCount: bpa.readyForReleaseCount,
              })
            : undefined;
          return (
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                textAlign: "right" as const,
                display: "block",
                fontVariantNumeric: "tabular-nums",
                color: pct !== undefined && pct > 0 ? getTrafficLightColor(pct) : undefined,
              }}
            >
              {pct === undefined ? "—" : `${pct}%`}
            </span>
          );
        },
      },
      {
        key: "bar",
        width: "220px",
        render: (bpa: BpaRow) => {
          if (!bpa.hasStats) return null;

          return (
            <CmsStackedBar
              variant="review-status"
              reviewStatusCounts={bpaReviewCounts({
                pendingCount: bpa.pendingCount,
                readyForEditingCount: bpa.readyForEditingCount,
                readyForReleaseCount: bpa.readyForReleaseCount,
              })}
              complete={bpa.approvedCount}
              inProgress={bpa.submissionCount - bpa.approvedCount}
              total={bpa.submissionCount}
            />
          );
        },
      },
    ],
    [],
  );

  const clearAll = useCallback(() => {
    setCurrentPage(1);
    setSearchInput("");
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("rankingEntity");
      next.delete("status");
      next.delete("search");
      next.delete("sort");
      return next;
    });
  }, [setSearchParams]);

  if (!hasPermission("staff.research.read")) return <UnauthorizedView />;

  const canManage = Boolean(manageTarget);

  const hasActiveFilters = Boolean(debouncedSearch || selectedRankingEntityId || statusFilter);

  if (bpaLoading && !bpaData) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Practice Areas" subtitle="Loading..." />
      </CmsLayout>
    );
  }

  if (error) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Failed to load practice areas" subtitle="" />
        <div style={{ padding: 24, color: "var(--color-danger)" }}>{error.message}</div>
      </CmsLayout>
    );
  }

  return (
    <>
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Practice Areas" subtitle={`${pagination.total} practice areas`}>
          <div className={styles.headerActions}>
            {hasTranslations && (
              <LanguageSwitcher
                outputLanguage={outputLanguage}
                activeLanguage={showTranslation ? "en-GB" : outputLanguage}
                onSwitch={lang => {
                  const next = new URLSearchParams(searchParams);
                  if (lang === "en-GB") next.set("lang", "en-GB");
                  else next.delete("lang");
                  setSearchParams(next, { replace: true });
                }}
              />
            )}
            {canAddBpa && (
              <button
                type="button"
                className={styles.addBpaButton}
                onClick={() => setShowAddBpa(true)}
              >
                + Add BPA
              </button>
            )}
            {canManage && manageTarget && (
              <button
                type="button"
                className={styles.manageLink}
                onClick={() =>
                  navigate(
                    ROUTES.research.cmsManage(
                      manageTarget.publicationConfigId,
                      manageTarget.editionConfigId,
                    ),
                  )
                }
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Manage
              </button>
            )}
          </div>
        </PageHeader>
        <div className={styles.wrapper}>
          {importResult && (
            <div className={styles.importBanner} role="status">
              {importResult}
              <button
                type="button"
                className={styles.importBannerDismiss}
                onClick={() => setImportResult(null)}
              >
                ×
              </button>
            </div>
          )}
          {importError && (
            <div className={styles.importBannerError} role="alert">
              {importError}
              <button
                type="button"
                className={styles.importBannerDismiss}
                onClick={() => setImportError(null)}
              >
                ×
              </button>
            </div>
          )}
          <SearchBar
            value={searchInput}
            onChange={setSearchInput}
            onClear={() => {
              setSearchInput("");
              clearParam("search");
            }}
            placeholder="Search practice areas..."
          />

          <CmsFilterRow
            filters={[
              {
                key: "rankingEntity",
                label: "Ranking Entity",
                value: selectedRankingEntityId,
                onChange: val => setParam("rankingEntity", val),
                options: rankingEntityOptions,
                placeholder: "All ranking entities",
                searchable: true,
              },
              {
                key: "status",
                label: "Status",
                value: statusFilter,
                onChange: val => setParam("status", val),
                options: [
                  { value: "not_started", label: "Not Started" },
                  { value: "researching", label: "Researching" },
                  { value: "editing", label: "Editing" },
                  { value: "complete", label: "Complete" },
                  { value: "archived", label: "Archived" },
                ],
                placeholder: "All statuses",
              },
              {
                key: "sort",
                label: "Sort by",
                value: sortBy,
                onChange: val => setParam("sort", val),
                options: [
                  { value: "name_asc", label: "Name (A-Z)" },
                  { value: "name_desc", label: "Name (Z-A)" },
                  { value: "status_asc", label: "Status (Not Started first)" },
                  { value: "status_desc", label: "Status (Complete first)" },
                ],
                placeholder: "",
              },
            ]}
            activeFilters={activeFilters}
            onClearFilter={clearParam}
            onClearAll={clearAll}
          />

          {bpas.length === 0 && !bpaLoading ? (
            <CmsEmptyState
              message={
                hasActiveFilters
                  ? "No practice areas match your filters."
                  : "No practice areas assigned to this edition."
              }
              action={
                !hasActiveFilters && canImport && hasImportableEditions
                  ? {
                      label: "Import BPAs from previous edition",
                      onClick: openImportDialog,
                    }
                  : undefined
              }
            />
          ) : (
            <div
              aria-busy={isRefetching}
              className={`${styles.tableContainer} ${isRefetching ? styles.tableContainerLoading : ""}`}
            >
              <CmsGroupedTable
                groups={tableGroups}
                columns={columns}
                onRowClick={bpa =>
                  navigate(ROUTES.research.cmsBpa(editionId!, bpa.bpaId), {
                    state: {
                      bpaName: bpa.bpaName,
                      rankingEntityName: bpa.rankingEntityName,
                      editionName,
                      directoryId: editionData?.directoryId ?? undefined,
                      templateType,
                    },
                  })
                }
                rowKey={bpa => bpa.bpaId}
              />

              {ungroupedTableGroup.length > 0 && (
                <div className={styles.ungroupedSection}>
                  <CmsGroupedTable
                    groups={ungroupedTableGroup}
                    columns={columns}
                    onRowClick={bpa =>
                      navigate(ROUTES.research.cmsBpa(editionId!, bpa.bpaId), {
                        state: {
                          bpaName: bpa.bpaName,
                          rankingEntityName: bpa.rankingEntityName,
                          editionName,
                          templateType,
                        },
                      })
                    }
                    rowKey={bpa => bpa.bpaId}
                  />
                </div>
              )}

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={pagination.total}
                itemName="practice areas"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </CmsLayout>

      {showImportDialog && (
        <ImportBpasDialog
          targetEditionName={editionName}
          publications={importPublications}
          selectedEditionConfigId={importSourceEditionConfigId}
          onSelectEdition={setImportSourceEditionConfigId}
          isImporting={isImporting}
          onImport={handleImport}
          onCancel={() => setShowImportDialog(false)}
        />
      )}

      <AddBpaModal
        open={showAddBpa}
        onClose={() => setShowAddBpa(false)}
        publications={(publicationsData ?? []).map(p => ({
          name: p.name,
          directoryId: p.directoryId,
        }))}
        currentDirectoryId={editionData?.directoryId ?? null}
        linkedBpaIds={linkedBpaIds}
        onLink={async bpaId => {
          await researchCms.linkEditionBpas({ editionId, bpaIds: [bpaId] });
          queryClient.invalidateQueries({
            queryKey: ["research-edition-bpas"],
          });
          queryClient.invalidateQueries({ queryKey: ["research-bpa-stats"] });
        }}
        onCreateNew={() => {
          if (!manageTarget) return;
          navigate(
            ROUTES.research.cmsCreateBpa(
              manageTarget.publicationConfigId,
              manageTarget.editionConfigId,
            ),
          );
        }}
      />
    </>
  );
}
